import React from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button, Modal } from "react-bootstrap";
import axios from "axios";
import useRoomData from "../../hooks/useRoomData";
import useRoomAnalysis from "../../hooks/useRoomAnalysis";
import useArtisanDiscovery from "../../hooks/useArtisanDiscovery";
import RoomEditor from "./RoomEditor";
import RoomViewer from "./RoomViewer";
import "./RoomDetail.css";
import LoadingOverlay from "../../components/LoadingOverlay/LoadingOverlay";
import supabase from "../../supabase";
import { useNotification } from "../../context/NotificationContext";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import ImageCarousel from "../../components/ImageCarousel/ImageCarousel";
import ImageUploader from "../../components/ImageUploader/ImageUploader";

const RoomDetail = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [analysisToDelete, setAnalysisToDelete] = React.useState(null);
  const [showPhotosModal, setShowPhotosModal] = React.useState(false);

  const {
    room,
    images,
    setImages,
    defaultImageIndex,
    setDefaultImageIndex,
    name,
    setName,
    superficie,
    setSuperficie,
    isEditing,
    setIsEditing,
    handleSaveRoom,
    loading,
    userPlan,
    analyses,
    setAnalyses,
  } = useRoomData();

  const [selectedAnalysis, setSelectedAnalysis] = React.useState(null);
  const [isNewAnalysisMode, setIsNewAnalysisMode] = React.useState(true);
  const [generatedVisuals, setGeneratedVisuals] = React.useState([]);
  const [imageModifications, setImageModifications] = React.useState([]);
  const [loadingImageModifications, setLoadingImageModifications] = React.useState(false);

  React.useEffect(() => {
    if (analyses && analyses.length > 0) {
      setSelectedAnalysis(analyses[0]);
      setIsNewAnalysisMode(false);
    } else {
      setIsNewAnalysisMode(true);
    }
    setGeneratedVisuals([]); // Clear visuals when analysis changes
  }, [analyses]);

  const fetchImageModifications = React.useCallback(async () => {
    if (!room?.id) {
      setImageModifications([]);
      return;
    }
    try {
      setLoadingImageModifications(true);
      const { data } = await axios.get(`${process.env.REACT_APP_SERVER_URL}/google/image-modifications/${room.id}`);
      setImageModifications(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch Google image modifications:", error);
    } finally {
      setLoadingImageModifications(false);
    }
  }, [room?.id]);

  React.useEffect(() => {
    fetchImageModifications();
  }, [fetchImageModifications]);

  React.useEffect(() => {
    if (!isNewAnalysisMode && selectedAnalysis) {
      const visualsForAnalysis = imageModifications
        .filter((mod) => mod && String(mod.analysis_id) === String(selectedAnalysis.id) && mod.modified_image_url)
        .map((mod) => ({
          original: mod.base_image_url || mod.modified_image_url,
          modified: mod.modified_image_url,
          analysisId: mod.analysis_id,
          id: mod.id,
        }));
      setGeneratedVisuals(visualsForAnalysis);
    } else if (isNewAnalysisMode) {
      setGeneratedVisuals([]);
    }
  }, [isNewAnalysisMode, selectedAnalysis, imageModifications]);

  const {
    handleAnalyzeRoom,
    analysisResult: newAnalysisResult,
    analyzing,
    comment: newAnalysisComment,
    setComment: setNewAnalysisComment,
    analysisType,
    hasEnoughCredits,
    isGeneratingVisuals,
  } = useRoomAnalysis(
    room,
    superficie,
    isNewAnalysisMode ? "" : selectedAnalysis?.user_comment,
    isNewAnalysisMode ? null : selectedAnalysis?.analysis_result,
    userPlan,
    room?.property_localisation,
    room?.surface_habitable_totale_logement_m2,
    room?.age_logement_annees
    
  );
  const { artisans, showArtisans, loadingArtisans, handleShowArtisans, artisansRef } =
    useArtisanDiscovery(room, selectedAnalysis);

  const handleBack = () => navigate(-1);

  const handleDeleteAnalysis = (analysisId) => {
    setAnalysisToDelete(analysisId);
    setShowDeleteModal(true);
  };

  const confirmDeleteAnalysis = async () => {
    if (!analysisToDelete) return;
    const { data, error } = await supabase
      .from("room_analyses")
      .delete()
      .eq("id", analysisToDelete)
      .select();

    if (error) {
      showNotification("Erreur lors de la suppression de l'analyse.", "danger");
      console.error("Error deleting analysis:", error);
    } else if (data && data.length > 0) {
      showNotification("Analyse supprimée avec succès.", "success");
      const updatedAnalyses = analyses.filter((a) => a.id !== analysisToDelete);
      setAnalyses(updatedAnalyses);
      if (selectedAnalysis?.id === analysisToDelete) {
        if (updatedAnalyses.length > 0) {
          setSelectedAnalysis(updatedAnalyses[0]);
        } else {
          setSelectedAnalysis(null);
          setIsNewAnalysisMode(true);
          setNewAnalysisComment("");
        }
      }
    } else {
      showNotification(
        "La suppression a échoué. Vérifiez les permissions de la base de données (RLS).",
        "warning"
      );
    }

    setShowDeleteModal(false);
    setAnalysisToDelete(null);
  };

  if (loading) return <p className="text-center">Chargement des données...</p>;
  if (!room) return <p className="text-center text-danger">Pièce non trouvée</p>;

  return (
    <div className="container mt-4 room-detail-container">
      <ConfirmationModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={confirmDeleteAnalysis}
        title="Confirmer la suppression"
        body="Êtes-vous sûr de vouloir supprimer cette analyse ? Cette action est irréversible."
      />

      <div className="d-flex align-items-center justify-content-between mb-3">
        <button className="btn btn-link text-decoration-none p-0 back-link" onClick={handleBack}>
          <i className="bi bi-arrow-left me-2" aria-hidden="true"></i>
          Retour
        </button>
      </div>

      {(analyzing || isGeneratingVisuals) && <LoadingOverlay message={isGeneratingVisuals ? "Génération des ambiances visuelles..." : "Analyse de la pièce en cours..."} />} 

      {/* Title and meta */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <h2 className="mb-0">{room.name}</h2>
        <div className="d-flex align-items-center gap-2">
          {room.property_localisation && (
            <span className="badge bg-light text-dark border">
              <i className="bi bi-geo-alt me-1" aria-hidden="true"></i>
              {room.property_localisation}
            </span>
          )}
          <span className="badge bg-light text-dark border">
            <i className="bi bi-aspect-ratio me-1" aria-hidden="true"></i>
            {room.superficie} m²
          </span>
          <span className="badge bg-light text-dark border">
            <i className="bi bi-image me-1" aria-hidden="true"></i>
            {images?.length || 0} images
          </span>
          <span className="badge bg-light text-dark border">
            <i className="bi bi-graph-up me-1" aria-hidden="true"></i>
            {analyses?.length || 0} analyses
          </span>
        </div>
      </div>

      {/* Main row */}
      <div className="row g-4 align-items-stretch">
        <div className="col-12 col-lg-8 d-flex">
          <Card className="property-info-card h-100 w-100 d-flex flex-column">
            <Card.Body className="d-flex flex-column">
              <ImageCarousel images={images} />
            </Card.Body>
          </Card>
        </div>
        <div className="col-12 col-lg-4 d-flex">
          {!isEditing ? (
            <Card className="property-info-card h-100 w-100 d-flex flex-column">
              <Card.Body className="d-flex flex-column justify-content-between">
                <div className="mb-3">
                  <div className="text-muted small mb-1">Superficie</div>
                  <div className="fw-semibold">{room.superficie ?? "—"} m²</div>
                </div>
                <div className="d-grid gap-2 mt-auto">
                  <button className="btn btn-primary" onClick={() => setIsEditing(true)}>
                    <i className="bi bi-pencil me-1" aria-hidden="true"></i>
                    Modifier les informations
                  </button>
                  <button className="btn btn-outline-secondary" onClick={() => setShowPhotosModal(true)}>
                    <i className="bi bi-images me-1" aria-hidden="true"></i>
                    Gérer les photos
                  </button>
                </div>
              </Card.Body>
            </Card>
          ) : (
            <Card className="property-info-card h-100 w-100 d-flex flex-column">
              <Card.Body className="d-flex flex-column">
                <RoomEditor
                  name={name}
                  setName={setName}
                  superficie={superficie}
                  setSuperficie={setSuperficie}
                  handleSaveRoom={handleSaveRoom}
                  setIsEditing={setIsEditing}
                />
                <div className="d-grid gap-2 mt-auto">
                  <button className="btn btn-outline-secondary" onClick={() => setShowPhotosModal(true)}>
                    <i className="bi bi-images me-1" aria-hidden="true"></i>
                    Gérer les photos
                  </button>
                </div>
              </Card.Body>
            </Card>
          )}
        </div>
      </div>

      {/* Analysis section */}
      <div className="mt-4">
        <RoomViewer
          room={room}
          images={images}
          comment={selectedAnalysis?.user_comment || newAnalysisComment}
          setComment={setNewAnalysisComment}
          analysisResult={selectedAnalysis?.analysis_result || newAnalysisResult}
          analysisType={selectedAnalysis?.analysis_type || analysisType}
          analyzing={analyzing}
          onEdit={() => setIsEditing(true)}
          onAnalyze={async (type, options) => {
            setGeneratedVisuals([]); // Clear previous visuals
            const result = await handleAnalyzeRoom(images, superficie, type, options);
            if (result.success) {
              if (typeof setAnalyses === "function") {
                setAnalyses((prevAnalyses) => [result.analysis, ...prevAnalyses]);
              }
              setSelectedAnalysis(result.analysis);
              setGeneratedVisuals(result.visuals || []);
              setIsNewAnalysisMode(false);
              await fetchImageModifications();
            }
          }}
          artisans={artisans}
          showArtisans={showArtisans}
          loadingArtisans={loadingArtisans}
          artisansRef={artisansRef}
          handleShowArtisans={handleShowArtisans}
          userPlan={userPlan}
          hasEnoughCredits={hasEnoughCredits}
          analyses={analyses}
          selectedAnalysis={selectedAnalysis}
          onSelectAnalysis={(analysis) => {
            setSelectedAnalysis(analysis);
            setIsNewAnalysisMode(false);
            setGeneratedVisuals([]); // Also clear when selecting a different analysis
          }}
          onDeleteAnalysis={handleDeleteAnalysis}
          isNewAnalysisMode={isNewAnalysisMode}
          onStartNewAnalysis={() => {
            setSelectedAnalysis(null);
            setIsNewAnalysisMode(true);
            setNewAnalysisComment("");
            setGeneratedVisuals([]);
          }}
          imageModifications={imageModifications}
          loadingImageModifications={loadingImageModifications}
          embedded
        />
      </div>

      {/* Photos modal */}
      <Modal show={showPhotosModal} onHide={() => setShowPhotosModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Gérer les photos de la pièce</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ImageUploader
            images={images}
            setImages={setImages}
            defaultImageIndex={defaultImageIndex}
            setDefaultImageIndex={setDefaultImageIndex}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPhotosModal(false)}>
            Fermer
          </Button>
          <Button className="btn btn-primary" onClick={handleSaveRoom}>
            Enregistrer
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RoomDetail;
